import type { VoiceCallSession } from '../types/voice-call'

/**
 * What the Android hardware Back press may do while a call exists.
 *
 * `App.exitApp()` finishes the activity, which destroys the WebView — and the
 * Chime meeting, the heartbeats and the END signal with it. A Back press on the
 * root route during a call therefore killed the call outright while the peer
 * stayed connected to nobody (dcfc5c9a, 2026-10-08). During a call Back may
 * only collapse the call UI, navigate inside the app, or background the task.
 *
 * - `minimize-call-overlay`: the full-screen 1:1 voice overlay is showing —
 *   collapse it to the return-to-call banner (same as its minimise button).
 * - `minimize-app`: background the task (`moveTaskToBack`); the call keeps
 *   running under the ongoing-call foreground service.
 * - `in-call`: normal handler / history navigation, but at the root the app is
 *   backgrounded instead of exited.
 * - `default`: no live call — unchanged behaviour.
 */
export type CallAwareBackAction = 'minimize-call-overlay' | 'minimize-app' | 'in-call' | 'default'

type BackSession = Pick<
  VoiceCallSession,
  'phase' | 'mediaType' | 'isGroup' | 'transferRoom' | 'userMinimized'
>

export function decideCallAwareBack(session: BackSession | null | undefined): CallAwareBackAction {
  if (!session) return 'default'
  const { phase } = session
  if (phase === 'idle' || phase === 'ended' || phase === 'elsewhere') return 'default'
  // The ring screen cannot be minimised, and history navigation underneath it
  // is invisible; background the app and let the native ring keep going.
  if (phase === 'incoming') return 'minimize-app'
  const voiceOverlayShown =
    session.mediaType !== 'video' && (!session.isGroup || Boolean(session.transferRoom))
  const overlayMinimisable = phase === 'active' || phase === 'connecting' || phase === 'outgoing'
  if (voiceOverlayShown && overlayMinimisable && !session.userMinimized) {
    return 'minimize-call-overlay'
  }
  return 'in-call'
}
